import Head from 'next/head';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import AgentFaq from '@/components/agents/AgentFaq';
import AgentFinalCta from '@/components/agents/AgentFinalCta';
import AgentLogos from '@/components/agents/AgentLogos';
import AgentReviews from '@/components/agents/AgentReviews';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import IndustryHero from './IndustryHero';
import IndustryJobs from './IndustryJobs';
import IndustryLinks from './IndustryLinks';
import IndustrySpotlight from './IndustrySpotlight';
import IndustryWorkflow from './IndustryWorkflow';
import type { IndustryContent } from './types';
import { useIndustryCatalog } from '../industryCatalog';
import { tone } from '../primitives';

const SITE = 'https://easy-peasy.ai';

/*
 * An industry page under /chatbots. Story: what the agent does in this
 * industry (hero), the questions it takes over (jobs), what it is built from
 * (workflow), the one capability that matters most here (spotlight), then
 * proof, questions and the way on to the other industries.
 */
export default function IndustryPage({
  content,
}: {
  content: IndustryContent;
}) {
  const { t, lang } = useLocale('bot-use-cases');
  const { locale } = useRouter();
  const entry = useIndustryCatalog().find(({ key }) => key === content.key);
  const label = entry?.label ?? content.seoTitle;
  const prefix = locale && locale !== 'en' ? `/${locale}` : '';

  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('AI Agents'),
        item: `${SITE}${prefix}/chatbots`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: label,
        item: `${SITE}${prefix}/chatbots/${content.key}`,
      },
    ],
  };

  return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main
      className={clsxm('relative overflow-x-clip', tone.page)}
      dir={lang === 'ar' ? 'rtl' : undefined}
    >
      <Seo
        templateTitle={content.seoTitle}
        description={content.seoDescription}
        image={`${SITE}/api/og-template?title=${encodeURIComponent(
          content.hero.title,
        )}`}
      />
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbs).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <IndustryHero label={label} hero={content.hero} />
      <AgentLogos />
      <IndustryJobs jobs={content.jobs} />
      <IndustryWorkflow workflow={content.workflow} />
      <IndustrySpotlight spotlight={content.spotlight} />
      <AgentReviews />
      <AgentFaq faqs={content.faqs} />
      <IndustryLinks current={content.key} />
      <AgentFinalCta
        title={content.finalTitle}
        exchange={content.finalExchange}
      />
      <Footer />
    </main>
  );
}
